import { WithAppStore } from "@/store/types";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  override,
  runInAction,
} from "mobx";
import {
  IndexedNoteSyncUpdateValue,
  INoteObservable,
  NoteContentDocumentModelData,
  NoteContentVersion,
  NoteMediaKind,
  NoteModelData,
  NoteSuggestionData,
} from "@/store/note/types";
import { getRenderableSuggestionData } from "@/store/note/suggestion-data";
import { Uuid } from "@/domains/global/identifiers";
import { VoiceSuggestionMissingAudioRecordingIdError } from "@/domains/errors";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import {
  GrantableSyncScopeRoleKind,
  SyncModelPermissionEntryWithStatus,
} from "@/domains/sync-scopes/types";
import { resolveFavoriteItemSyncModelUuid } from "@/modules/uuid/sync-models/resolveFavoriteItemSyncModelUuid";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { filter, isEqual, uniq } from "lodash-es";
import { NoteCollectionListObservable } from "@/store/collection-items/NoteCollectionListObservable";
import { NoteCreateGateDeniedError } from "@/store/note/AppStoreNoteStore";
import { resolveSpaceAccountNoteSyncModelUuid } from "@/modules/uuid/sync-models/resolveSpaceAccountNoteSyncModelUuid";
import { SpaceAccountNoteObservable } from "@/store/recent-items/SpaceAccountNoteObservable";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { FavoriteItemObservable } from "@/store/favorite-items/FavoriteItemObservable";
import { IContactModel } from "@/store/contacts/types";
import { getQueuedDocumentUpdates } from "@/store/sync/operations/helpers/notes/getQueuedDocumentUpdates";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { DeleteNoteOperation } from "@/store/sync/operations/notes/DeleteNoteOperation";
import { TrashNoteOperation } from "@/store/sync/operations/notes/TrashNoteOperation";
import { RestoreNoteOperation } from "@/store/sync/operations/notes/RestoreNoteOperation";
import { AddNoteToFavoritesOperation } from "@/store/sync/operations/favorites/AddNoteToFavoritesOperation";
import { RemoveNoteFromFavoritesOperation } from "@/store/sync/operations/favorites/RemoveNoteFromFavoritesOperation";
import { MarkNoteViewedOperation } from "@/store/sync/operations/recents/MarkNoteViewedOperation";
import { GrantNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/GrantNoteAclViaSpaceAccountOperation";
import { GrantNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/GrantNoteAclViaEmailAddressOperation";
import { UpdateNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaSpaceAccountOperation";
import { UpdateNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaEmailAddressOperation";
import { UpdateNoteAclViaCollectionOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaCollectionOperation";
import { RevokeNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/RevokeNoteAclViaSpaceAccountOperation";
import { RevokeNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/RevokeNoteAclViaEmailAddressOperation";
import { logger } from "@/modules/logger";
import { getPermissionsForNoteSyncModel } from "@/store/sync/operations/helpers/permissions/getPermissionsForModel";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { NoteContentDocumentObservable } from "@/store/note/NoteContentDocumentObservable";
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from "@/domains/async/AsyncData";
import { appRoutes } from "@/app/router";
import { notesModule } from "@/modules/notes";
import { uuidModule } from "@/modules/uuid";
import {
  ExtractedNoteMention,
  MemCommonEditorAssetProxyConfig,
  MemCommonEditorContext,
  MemCommonEditorFileInfo,
  MemCommonEditorImageInfo,
  yjsModule,
} from "@mem-labs/common-editor";
import { UploadImageExternalOperation } from "@/store/sync/operations/external/UploadImageExternalOperation";
import { UploadFileExternalOperation } from "@/store/sync/operations/external/UploadFileExternalOperation";
import { ExternalOperationKind } from "@/store/sync/operations/types";
import { EventContext } from "@/domains/metrics/context";
import { JsonValue } from "type-fest";
import { liveQuery, Subscription } from "dexie";
import { UpdateNoteContentUsingTemplateAndDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingTemplateAndDiffOperation";
import { NoteSourceListObservable } from "@/store/sources/NoteSourceListObservable";
import { UpdateNoteContentOverwritingFullStateDestructivelyOperation } from "@/store/sync/operations/notes/UpdateNoteContentOverwritingFullStateDestructivelyOperation";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { AcceptNoteSuggestionOperation } from "@/store/sync/operations/notes/AcceptNoteSuggestionOperation";
import { RejectNoteSuggestionOperation } from "@/store/sync/operations/notes/RejectNoteSuggestionOperation";
import { cleanupNote } from "@/actions/cleanupNote";
import { refineNote } from "@/actions/refineNote";
import { refineNoteWithVoice } from "@/actions/refineNoteWithVoice";
import { truncateSuggestionDataForTracking } from "@/store/note/helpers/suggestionTracking";
import { AutoCleanupResult } from "@/modules/notes/auto-cleanup-policy";
import { getBackendAssetProxyUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { resolveSpaceAccountCalendarEventSyncModelUuid } from "@/modules/uuid/sync-models/resolveSpaceAccountCalendarEventSyncModelUuid";
import { resolveSmartContextId } from "@/store/heads-up/SmartContextId";
import { SmartViewKind } from "@/domains/smart/constants";

export class NoteObservable extends BaseSyncModel<NoteModelData> implements INoteObservable {
  private static readonly NOTE_CONTENT_REMOTE_SYNC_REASONS = {
    IsLoaded: "is-loaded",
    RemoteContent: "remote-content",
  } as const;

  public collectionList: NoteCollectionListObservable;
  public sourceList: NoteSourceListObservable;
  public noteContentDocument: AsyncData<NoteContentDocumentObservable> = asyncDataLoading({});
  public modelKind = SyncModelKind.Note;

  // INDEXES
  public createdAt: string;
  public modifiedAt: string;
  public trashedAt: string | null;
  public lastViewedAt: string;
  public isTrashed: boolean;

  private primaryLabel: string = "";
  private secondaryLabel: string = "";
  private locked: boolean = false;
  private noteContentDocumentRemoteSubscription?: Subscription;
  private readonly noteContentDocumentRemoteSyncReasons = new Set<
    (typeof NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS)[keyof typeof NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS]
  >();

  isProcessingSuggestion: boolean = false;

  constructor({
    id,
    data,
    store,
  }: {
    id: Uuid;
    data: IndexedNoteSyncUpdateValue;
  } & WithAppStore) {
    super({ id, data, store });
    this.collectionList = new NoteCollectionListObservable({ noteId: id, store });
    this.sourceList = new NoteSourceListObservable({ noteId: id, store });

    this.createdAt = data.created_at;
    this.modifiedAt = data.modified_at;
    this.trashedAt = data.trashed_at || null;
    this.lastViewedAt = data.last_viewed_at;
    this.isTrashed = data.is_trashed === 1;
    this.primaryLabel = data.primary_label || "";
    this.secondaryLabel = data.model_data.secondary_label || "";

    makeObservable<
      NoteObservable,
      | "primaryLabel"
      | "locked"
      | "secondaryLabel"
      | "updateLabels"
      | "notifyHeadsUpOfAutoGenerationReadiness"
      | "refreshNoteContentDocumentObservable"
      | "noteContentDocumentRemoteSubscription"
      | "noteContentDocumentRemoteSyncReasons"
      | "activateNoteContentDocumentRemoteSync"
      | "deactivateNoteContentDocumentRemoteSync"
      | "subscribeToNoteContentDocumentRemote"
      | "unsubscribeFromNoteContentDocumentRemote"
      | "buildAssetProxyConfigForContext"
      | "persistedSuggestionData"
    >(this, {
      modelKind: observable,
      // INDEXES
      createdAt: observable,
      modifiedAt: observable,
      trashedAt: observable,
      lastViewedAt: observable,
      isTrashed: observable,

      updateFromLocal: override,
      updateFromData: action,
      updateFromRemote: override,
      subscribeToLocal: override,

      // OBSERVABLES
      noteContentDocument: observable,
      collectionList: observable,
      sourceList: observable,

      // OVERRIDES
      isAvailable: override,
      isShared: override,
      permissions: override,

      // PROPERTIES
      memoryQueue: computed,
      receivedAt: computed,
      lastMentionedAt: computed,
      locallyCreatedAt: computed,
      title: computed,
      secondaryTitle: computed,
      path: computed,
      primaryOwner: computed,
      authors: computed,
      metadata: computed,
      shared_with_me_at: computed,
      primaryLabel: observable,
      secondaryLabel: observable,
      locked: observable,
      importBatchId: computed,
      hasSources: computed,
      primaryLabelText: computed,
      hasPrimaryLabel: computed,
      notifyHeadsUpOfAutoGenerationReadiness: false,
      refreshNoteContentDocumentObservable: false,
      noteContentDocumentRemoteSubscription: false,
      noteContentDocumentRemoteSyncReasons: false,
      activateNoteContentDocumentRemoteSync: false,
      deactivateNoteContentDocumentRemoteSync: false,
      subscribeToNoteContentDocumentRemote: false,
      unsubscribeFromNoteContentDocumentRemote: false,

      // NOTE CONTENT
      noteContentDocumentId: computed,
      setNoteContentDocument: action,
      isNoteContentDocumentLoaded: computed,
      fetchNoteContentDocument: action,
      refreshNoteContentDocument: action,
      remoteContent: computed,
      isNoteContentInOriginalState: computed,
      queuedDocumentUpdates: computed,

      // FAVORITES
      favoriteItemId: computed,
      favoriteItem: computed,
      isFavorited: computed,
      isLocked: computed,

      // CALENDAR EVENT
      hasCalendarEventSource: computed,
      associatedSpaceAccountCalendarEventId: computed,
      associatedCalendarEvent: computed,
      associatedCalendarEventId: computed,

      // SPACE ACCOUNT
      spaceAccountNoteId: computed,
      spaceAccountNote: computed,

      // ACTIONS
      updateContentUsingDiff: false,
      acceptNoteContentChanges: false,
      saveAsNewNote: action,
      delete: action,
      deleteEmptyNote: action,
      moveToTrash: action,
      restoreFromTrash: action,
      grantAccessViaSpaceAccount: action,
      grantAccessViaEmailAddress: action,
      updateAccessViaSpaceAccount: action,
      updateAccessViaEmailAddress: action,
      updateAccessViaCollection: action,
      revokeAccessViaSpaceAccount: action,
      revokeAccessViaEmailAddress: action,
      toggleFavorite: action,
      addToRecents: action,
      uploadImageAssociatedWithNote: action,
      uploadFileAssociatedWithNote: action,
      generateCommonEditorContext: action,
      lock: action,
      unlock: action,
      updateLabels: action,
      buildAssetProxyConfigForContext: false,

      // Suggestions
      persistedSuggestionData: false,
      isProcessingSuggestion: observable,
      setIsProcessingSuggestion: action,
      isSuggestionActive: computed,
      suggestionData: computed,
      rejectSuggestion: action,
      acceptSuggestion: action,
      refineNoteSuggestion: action,
      cleanup: action,

      // Audio
      associatedAudioRecording: computed,
      isAudioRecordingActive: computed,
      isAwaitingAudioRecordingTransformation: computed,
    });

    onBecomeObserved(this, "isNoteContentDocumentLoaded", () => {
      this.activateNoteContentDocumentRemoteSync(
        NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS.IsLoaded
      );
      this.fetchNoteContentDocument();
    });
    onBecomeUnobserved(this, "isNoteContentDocumentLoaded", () => {
      this.deactivateNoteContentDocumentRemoteSync(
        NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS.IsLoaded
      );
    });
    onBecomeObserved(this, "remoteContent", () => {
      this.activateNoteContentDocumentRemoteSync(
        NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS.RemoteContent
      );
    });
    onBecomeUnobserved(this, "remoteContent", () => {
      this.deactivateNoteContentDocumentRemoteSync(
        NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS.RemoteContent
      );
    });
  }

  async subscribeToLocal() {
    this.localSubscription?.unsubscribe();
    this.localSubscription = liveQuery(() => this.store.notes.localTable.get(this.id)).subscribe({
      next: data =>
        runInAction(() => {
          if (data) this.updateFromData(data);
        }),
    });
  }

  async updateFromLocal() {
    const data = await this.store.notes.localTable.get(this.id);
    if (data) runInAction(() => this.updateFromData(data));
  }

  updateFromData(data: IndexedNoteSyncUpdateValue) {
    this.data = data;
    this.createdAt = data.created_at;
    this.modifiedAt = data.modified_at;
    this.trashedAt = data.trashed_at || null;
    this.isTrashed = data.is_trashed === 1;
    this.lastViewedAt = data.last_viewed_at;
    if (!this.locked) {
      this.primaryLabel = data.primary_label || "";
      this.secondaryLabel = data.model_data.secondary_label || "";
    }

    if (this.existsRemotely) {
      this.store.headsUp.markNoteSyncedForAutoGeneration(this.id);
    }

    this.notifyHeadsUpOfAutoGenerationReadiness({
      primaryLabel: this.primaryLabel,
      isEmpty: Boolean(data.model_data.is_empty),
    });
  }

  async updateFromRemote() {
    await super.updateFromRemote();

    if (this.existsRemotely) {
      this.store.headsUp.markNoteSyncedForAutoGeneration(this.id);
    }
  }

  public lock() {
    logger.debug({
      message: `[NoteObservable][lock][debug] ${this.id} Locked.`,
    });

    this.locked = true;
  }

  public unlock() {
    logger.debug({
      message: `[NoteObservable][unlock][debug] ${this.id} Unlocked.`,
    });

    this.locked = false;
  }

  public get isLocked() {
    return this.locked;
  }

  // PROPERTIES
  get memoryQueue() {
    return this.store.notes.getNoteQueue({ noteId: this.id });
  }

  // TODO: Implement proper received_at when ready
  get receivedAt(): string | null | undefined {
    return this.modelData.created_at;
  }

  get lastMentionedAt(): string | undefined {
    return undefined;
  }

  get locallyCreatedAt(): string {
    return this.modelData.locally_created_at;
  }

  get isShared(): boolean {
    const output = super.isShared;
    for (const collection of this.collections) if (collection.isShared) return true;
    return output;
  }

  get shared_with_me_at(): string {
    return this.spaceAccountNote?.modelData.shared_with_me_at || "";
  }

  get isAvailable(): boolean {
    return !this.isDeleted && this.canAccess && !this.isTrashed;
  }

  get permissions(): SyncModelPermissionEntryWithStatus[] {
    return getPermissionsForNoteSyncModel({
      id: this.id,
      remoteData: this.remoteData,
      store: this.store,
      actionQueue: this.store.sync.actionQueue,
    });
  }

  get title(): string {
    // We need to access the modelData first to make sure that we subscribeToLocal when this is accessed
    const modelPrimaryLabel = this.modelData.primary_label;
    return this.primaryLabel || modelPrimaryLabel || UNTITLED_NOTE_TITLE;
  }

  get secondaryTitle(): string {
    // We need to access the modelData first to make sure that we subscribeToLocal when this is accessed
    const modelSecondaryLabel = this.modelData.secondary_label;
    return this.secondaryLabel || modelSecondaryLabel || "";
  }

  get hasPrimaryLabel(): boolean {
    if (this.primaryLabel.trim()) return true;
    return Boolean(this.modelData.primary_label?.trim());
  }

  get primaryLabelText(): string {
    // We need to access the modelData first to make sure that we subscribeToLocal when this is accessed
    const modelPrimaryLabel = this.modelData.primary_label;
    return this.primaryLabel || modelPrimaryLabel || "";
  }

  get path(): string {
    return appRoutes.notesView({ params: { noteId: this.id } }).path;
  }

  get primaryOwner(): IContactModel | undefined {
    const spaceAccountId = this.data.model_data.owned_by_space_account_id;
    const spaceAccount =
      spaceAccountId === this.store.spaceAccounts.myPersonalSpaceAccountId
        ? this.store.spaceAccounts.myPersonalSpaceAccount
        : this.store.contacts.getBySpaceAccountId(spaceAccountId);
    return spaceAccount;
  }

  get authors(): IContactModel[] {
    const spaceAccountIds = uniq([
      this.data.model_data.owned_by_space_account_id,
      ...this.data.model_data.modified_by_space_account_ids,
      ...this.remoteData.model_data.modified_by_space_account_ids,
    ]);
    const getContactObservableByContactSpaceAccountId = (spaceAccountId: string) => {
      if (spaceAccountId === this.store.spaceAccounts.myPersonalSpaceAccountId) {
        return this.store.spaceAccounts.myPersonalSpaceAccount;
      }
      return this.store.contacts.getBySpaceAccountId(spaceAccountId);
    };
    const contacts = spaceAccountIds.map(getContactObservableByContactSpaceAccountId);
    return filter(contacts) as IContactModel[];
  }

  get metadata(): string {
    // TODO: implement
    return "";
  }

  // NOTE CONTENT
  get noteContentDocumentId(): Uuid {
    return resolveNoteContentDocumentSyncModelUuid({ noteId: this.id });
  }

  setNoteContentDocument(noteContentDocument: NoteContentDocumentObservable) {
    if (
      this.noteContentDocument.data &&
      isEqual(this.noteContentDocument.data.id, noteContentDocument.id) &&
      isEqual(this.noteContentDocument.data.data, noteContentDocument.data) &&
      isEqual(this.noteContentDocument.data.remoteData, noteContentDocument.remoteData)
    ) {
      return;
    }

    this.noteContentDocument = asyncDataLoaded(this.noteContentDocument, noteContentDocument);
    if (this.noteContentDocumentRemoteSyncReasons.size > 0) {
      this.subscribeToNoteContentDocumentRemote(noteContentDocument);
    } else {
      this.unsubscribeFromNoteContentDocumentRemote();
    }
  }

  /**
   * Keeps the in-memory NoteContentDocumentObservable in sync with IndexedDB
   * while the note is actively being rendered.
   *
   * Without this, the content document observable can become stale: the sync
   * system writes new content to IndexedDB, but the in-memory observable still
   * holds old `remoteData` because its own BaseSyncModel liveQuery subscription
   * was inactive (nothing was observing `remoteData` while the note was closed).
   *
   * This subscription is scoped to active note-content consumers so pooled note
   * observables do not keep background Dexie watchers alive after the note closes.
   */
  private activateNoteContentDocumentRemoteSync(
    reason: (typeof NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS)[keyof typeof NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS]
  ) {
    if (this.noteContentDocumentRemoteSyncReasons.has(reason)) {
      return;
    }

    const hadActiveReasons = this.noteContentDocumentRemoteSyncReasons.size > 0;
    this.noteContentDocumentRemoteSyncReasons.add(reason);

    // subscribe to the note content document remote once we get a reason to do so
    if (!hadActiveReasons && this.noteContentDocument.data) {
      this.subscribeToNoteContentDocumentRemote(this.noteContentDocument.data);
    }
  }

  private deactivateNoteContentDocumentRemoteSync(
    reason: (typeof NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS)[keyof typeof NoteObservable.NOTE_CONTENT_REMOTE_SYNC_REASONS]
  ) {
    this.noteContentDocumentRemoteSyncReasons.delete(reason);

    if (this.noteContentDocumentRemoteSyncReasons.size === 0) {
      this.unsubscribeFromNoteContentDocumentRemote();
    }
  }

  private subscribeToNoteContentDocumentRemote(noteContentDocument: NoteContentDocumentObservable) {
    this.unsubscribeFromNoteContentDocumentRemote();

    this.noteContentDocumentRemoteSubscription = liveQuery(() =>
      this.store.memDb.mappedTables[SyncModelKind.NoteContentDocument].remote.get(
        noteContentDocument.id
      )
    ).subscribe({
      next: data =>
        runInAction(() => {
          if (data) {
            noteContentDocument.remoteData = data as SyncUpdateValue<NoteContentDocumentModelData>;
          }
        }),
    });
  }

  private unsubscribeFromNoteContentDocumentRemote() {
    this.noteContentDocumentRemoteSubscription?.unsubscribe();
    this.noteContentDocumentRemoteSubscription = undefined;
  }

  /**
   * Note content starts in the loading state.
   */
  get isNoteContentDocumentLoaded(): boolean {
    if (this.noteContentDocument.isLoading) return false;

    if (!this.noteContentDocument.data) {
      logger.info({
        message: `[NoteObservable][isNoteContentDocumentLoaded] Loaded, but no data.`,
        info: {
          noteId: this.id,
          noteContentDocumentId: this.noteContentDocumentId,
        },
      });

      return false;
    }

    return true;
  }

  get importBatchId(): string | null {
    return this.modelData.import_batch_id || null;
  }

  get hasSources(): boolean {
    return this.sourceList?.allSupportedSources.length > 0;
  }

  get hasCalendarEventSource(): boolean {
    return this.sourceList?.allCalendarEventSources.length > 0;
  }

  get associatedSpaceAccountCalendarEventId() {
    const connectedCalendarEventId =
      this.sourceList?.allCalendarEventSources[0]?.associatedArtifactId;
    if (!connectedCalendarEventId) return;
    const spaceAccountCalendarEventId = resolveSpaceAccountCalendarEventSyncModelUuid({
      connectedCalendarEventId,
    });
    return spaceAccountCalendarEventId;
  }

  get associatedCalendarEvent(): SpaceAccountCalendarEventObservable | undefined {
    const spaceAccountCalendarEventId = this.associatedSpaceAccountCalendarEventId;
    if (!spaceAccountCalendarEventId) return undefined;

    return this.store.spaceAccountCalendarEvents.get(spaceAccountCalendarEventId);
  }

  get associatedCalendarEventId(): string | null {
    return (
      this.associatedCalendarEvent?.associatedConnectedCalendarEventId ??
      this.sourceList?.allCalendarEventSources[0]?.associatedArtifactId ??
      null
    );
  }

  private async refreshNoteContentDocumentObservable(
    noteContentDocument: NoteContentDocumentObservable
  ) {
    await Promise.all([
      noteContentDocument.updateFromLocal(),
      noteContentDocument.updateFromRemote(),
    ]);
    this.setNoteContentDocument(noteContentDocument);
  }

  async fetchNoteContentDocument() {
    if (this.isNoteContentDocumentLoaded) return;

    const logInfo = {
      noteId: this.id,
      noteContentDocumentId: this.noteContentDocumentId,
    };

    logger.info({
      message: `[NoteObservable][fetchNoteContentDocument] Started.`,
      info: logInfo,
    });

    const noteContentDocument = await this.store.noteContentDocuments.getAsync(
      this.noteContentDocumentId
    );

    if (noteContentDocument) {
      this.setNoteContentDocument(noteContentDocument);

      logger.info({
        message: `[NoteObservable][fetchNoteContentDocument] Got from local.`,
        info: logInfo,
      });

      return;
    }

    runInAction(() => {
      this.noteContentDocument = asyncDataLoading(this.noteContentDocument);
    });

    const fetchedNoteContentDocument = await this.store.noteContentDocuments.fetch(
      this.noteContentDocumentId
    );

    if (fetchedNoteContentDocument) {
      this.setNoteContentDocument(fetchedNoteContentDocument);
    } else {
      runInAction(() => {
        this.noteContentDocument = asyncDataFailed(this.noteContentDocument);
      });
    }

    logger.info({
      message: `[NoteObservable][fetchNoteContentDocument] Fetched from remote.`,
      info: {
        ...logInfo,
        fetchResult: !!fetchedNoteContentDocument,
      },
    });
  }

  /**
   * Refreshes the note content from current synced state, even if it is
   * already loaded.
   */
  async refreshNoteContentDocument() {
    const logInfo = {
      noteId: this.id,
      noteContentDocumentId: this.noteContentDocumentId,
    };

    logger.info({
      message: `[NoteObservable][refreshNoteContentDocument] Started.`,
      info: logInfo,
    });

    if (this.noteContentDocument.data) {
      await this.refreshNoteContentDocumentObservable(this.noteContentDocument.data);

      logger.info({
        message: `[NoteObservable][refreshNoteContentDocument] Refreshed existing loaded content document.`,
        info: logInfo,
      });

      return;
    }

    const noteContentDocument = await this.store.noteContentDocuments.getAsync(
      this.noteContentDocumentId
    );

    if (noteContentDocument) {
      await this.refreshNoteContentDocumentObservable(noteContentDocument);

      logger.info({
        message: `[NoteObservable][refreshNoteContentDocument] Refreshed from local.`,
        info: logInfo,
      });

      return;
    }

    runInAction(() => {
      this.noteContentDocument = asyncDataLoading(this.noteContentDocument);
    });

    const fetchedNoteContentDocument = await this.store.noteContentDocuments.fetch(
      this.noteContentDocumentId
    );

    if (fetchedNoteContentDocument) {
      await this.refreshNoteContentDocumentObservable(fetchedNoteContentDocument);
    } else {
      runInAction(() => {
        this.noteContentDocument = asyncDataFailed(this.noteContentDocument);
      });
    }

    logger.info({
      message: `[NoteObservable][refreshNoteContentDocument] Fetched from remote.`,
      info: {
        ...logInfo,
        fetchResult: !!fetchedNoteContentDocument,
      },
    });
  }

  get remoteContent(): string | null {
    return this.noteContentDocument.data?.remoteContent || null;
  }

  get isNoteContentInOriginalState() {
    // Not in original state if there are things in the queue
    const queuedDocumentUpdates = this.queuedDocumentUpdates;
    if (queuedDocumentUpdates.length > 0) return false;

    // Versions:
    // 0: Created locally.
    // 1: Synced to server.
    // 2+: Content updates.
    return this.modelVersion <= 1;
  }

  get queuedDocumentUpdates() {
    return getQueuedDocumentUpdates({
      operationsByModelId: this.store.sync.actionQueue.operationsByModelId,
      id: this.id,
    });
  }

  // FAVORITES
  get favoriteItemId(): Uuid {
    return resolveFavoriteItemSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      itemId: this.id,
    });
  }

  get favoriteItem(): FavoriteItemObservable | undefined {
    return this.store.favoriteItems.get(this.favoriteItemId);
  }

  get isFavorited(): boolean {
    return this.store.favoriteItems.has(this.favoriteItemId);
  }

  // SPACE ACCOUNT NOTE
  get spaceAccountNoteId(): Uuid {
    return resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      noteId: this.id,
    });
  }

  get spaceAccountNote(): SpaceAccountNoteObservable | undefined {
    return this.store.spaceAccountNotes.get(this.spaceAccountNoteId);
  }

  // ACTIONS
  public updateContentUsingDiff({
    encodedContentDiff,
    primaryLabel,
    secondaryLabel,
    mediaKinds,
    isEmpty,
    transactionMetadata,
    tiptapJsonContent,
    mentions,
    templateId,
  }: {
    encodedContentDiff: string | null;
    primaryLabel: string;
    secondaryLabel: string;
    mediaKinds?: NoteMediaKind[];
    transactionMetadata?: Record<string, JsonValue>;
    tiptapJsonContent?: string;
    mentions?: ExtractedNoteMention[];
    templateId?: string;
    isEmpty?: boolean;
  }) {
    const noteIsEmpty = Boolean(isEmpty);

    if (!encodedContentDiff) {
      this.updateLabels(primaryLabel, secondaryLabel);
    } else {
      // TODO: Handle null properly
      this.memoryQueue.push(
        templateId
          ? new UpdateNoteContentUsingTemplateAndDiffOperation({
              store: this.store,
              payload: {
                note_id: this.id,
                encoded_content_diff: encodedContentDiff || "",
                template_id: templateId,
              },
              primaryLabel,
              secondaryLabel,
              mediaKinds,
              transactionMetadata,
              tiptapJsonContent,
              mentions,
            })
          : new UpdateNoteContentUsingDiffOperation({
              store: this.store,
              payload: {
                id: this.id,
                encoded_content_diff: encodedContentDiff || "",
                is_silent: transactionMetadata?.["addToHistory"] === false,
              },
              primaryLabel,
              secondaryLabel,
              mediaKinds,
              transactionMetadata,
              tiptapJsonContent,
              mentions,
              isEmpty: noteIsEmpty,
            }),
        encodedContentDiff ? { dropUniqueId: METADATA_ID } : { uniqueId: METADATA_ID }
      );
    }

    this.notifyHeadsUpOfAutoGenerationReadiness({ primaryLabel, isEmpty: noteIsEmpty });

    this.store.headsUp.handleEditorUpdate({
      noteId: this.id,
      primaryLabel,
      isEmpty: noteIsEmpty,
      transactionMetadata,
    });
  }

  public acceptNoteContentChanges(
    version: Omit<NoteContentVersion, "model_version">,
    baseVersion?: Omit<NoteContentVersion, "model_version">,
    args?: { overwrite?: boolean; patchId?: string }
  ) {
    const encodedContent = (() => {
      const encodedContent = version.model_data.encoded_content;
      if (!encodedContent) return;

      const baseEncodedContent = baseVersion?.model_data.encoded_content;
      if (baseEncodedContent) {
        return yjsModule.mergeDiffsWithRemoteContent(baseEncodedContent, [encodedContent]);
      }
      return encodedContent;
    })();

    if (!encodedContent) return;
    const { primaryLabel, secondaryLabel, mediaKinds, mentions } =
      notesModule.convertEncodedContentToNoteContent(encodedContent);

    const operation = args?.overwrite
      ? new UpdateNoteContentOverwritingFullStateDestructivelyOperation({
          store: this.store,
          payload: {
            id: this.id,
            encoded_content: encodedContent,
          },
          primaryLabel: primaryLabel || "",
          secondaryLabel: secondaryLabel || "",
          mediaKinds: mediaKinds || [],
          mentions: mentions || [],
        })
      : // TODO: send patchId to server, in the end this should look like UpdateNoteContentUsingTemplateAndDiffOperation
        new UpdateNoteContentUsingDiffOperation({
          store: this.store,
          payload: {
            id: this.id,
            encoded_content_diff: encodedContent,
            is_silent: false, // This needs to false to trigger an sync update for NoteSyncModel
          },
          primaryLabel: primaryLabel || "",
          secondaryLabel: secondaryLabel || "",
          mediaKinds: mediaKinds || [],
          transactionMetadata: { addToHistory: false },
          tiptapJsonContent: "",
          mentions: [],
          isEmpty: false,
        });

    this.memoryQueue.push(operation, { dropUniqueId: METADATA_ID });

    this.store.interface.notifyReloadRequired(this.id);
  }

  public async saveAsNewNote({ eventContext }: { eventContext: EventContext }) {
    const remoteContent = this.remoteContent;
    const diffs = this.queuedDocumentUpdates.map(update => update.encodedContentDiff);
    const newEncodedContent = yjsModule.mergeDiffsWithRemoteContent(remoteContent, diffs);
    const { primaryLabel, secondaryLabel, mediaKinds } =
      notesModule.convertEncodedContentToNoteContent(newEncodedContent);
    const newNoteId = uuidModule.generate();
    try {
      // `createNote` now throws `NoteCreateGateDeniedError` when the quota blocks creation.
      // Swallow it so the save-as-new flow simply stops without spamming logs.
      await this.store.notes.createNote({ noteId: newNoteId, eventContext });
    } catch (error) {
      if (error instanceof NoteCreateGateDeniedError) return;
      throw error;
    }
    const queue = this.store.notes.getNoteQueue({ noteId: newNoteId });
    queue.push(
      new UpdateNoteContentUsingDiffOperation({
        store: this.store,
        payload: {
          id: newNoteId,
          encoded_content_diff: newEncodedContent,
        },
        mediaKinds: mediaKinds || [],
        primaryLabel: primaryLabel || "",
        secondaryLabel: secondaryLabel || "",
      })
    );
  }

  public async delete() {
    this.memoryQueue.push(new DeleteNoteOperation({ store: this.store, payload: { id: this.id } }));
  }

  public async deleteEmptyNote() {
    const [hasSource, hasRecording] = await Promise.all([
      this.store.noteSources.hasAnySourceForNote({ noteId: this.id }),
      this.store.dexie.repositories.audioRecordings.hasAnyForNote({ noteId: this.id }),
    ]);

    const decision = notesModule.shouldAutoCleanUp({
      id: this.id,
      editor: { isReadOnly: false, isNoteEmpty: true },
      note: {
        isDeleted: this.isDeleted,
        isTrashed: this.isTrashed,
        isOwnedByMe: this.isOwnedByMe,
        isContentLoaded: this.isNoteContentDocumentLoaded,
        modelVersion: this.modelVersion,
        hasQueuedUpdates: this.queuedDocumentUpdates.length > 0,
        hasOpenTab: this.store.navigation.tabs.activeNoteIds.has(this.id),
      },
      runtime: {
        isAudioRecordingActive: this.isAudioRecordingActive,
        hasSuggestion: !!this.suggestionData,
        isAutoDeletable: this.store.notes.autoDeletableNoteIds.has(this.id),
        hasSource,
        hasRecording,
      },
    });

    if (decision.result === null) return;

    if (decision.result === AutoCleanupResult.DeletePermanently) {
      logger.info({ message: `[${this.id}] Deleting note` });
      await this.delete();
      return;
    }

    logger.info({ message: `[${this.id}] Trashing note` });
    this.moveToTrash();
  }

  public async moveToTrash(args?: { triggerSuccessToast?: boolean }) {
    this.memoryQueue.push(
      new TrashNoteOperation({
        ...args,
        store: this.store,
        payload: { id: this.id },
      })
    );
  }

  public async restoreFromTrash(args?: { triggerSuccessToast?: boolean }) {
    this.memoryQueue.push(
      new RestoreNoteOperation({
        ...args,
        store: this.store,
        payload: { id: this.id },
      })
    );
  }

  public async grantAccessViaSpaceAccount({
    roleKind,
    targetSpaceAccountId,
  }: {
    roleKind: GrantableSyncScopeRoleKind;
    targetSpaceAccountId: string;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new GrantNoteAclViaSpaceAccountOperation({
      store: this.store,
      payload: {
        id: this.id,
        space_account_id: targetSpaceAccountId,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async grantAccessViaEmailAddress({
    targetEmailAddress,
    roleKind,
  }: {
    targetEmailAddress: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new GrantNoteAclViaEmailAddressOperation({
      store: this.store,
      payload: {
        id: this.id,
        role_kind: roleKind,
        email_address: targetEmailAddress,
      },
    }).execute();
  }

  public async updateAccessViaSpaceAccount({
    targetSpaceAccountId,
    roleKind,
  }: {
    targetSpaceAccountId: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new UpdateNoteAclViaSpaceAccountOperation({
      store: this.store,
      payload: {
        id: this.id,
        space_account_id: targetSpaceAccountId,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async updateAccessViaEmailAddress({
    targetEmailAddress,
    roleKind,
  }: {
    targetEmailAddress: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new UpdateNoteAclViaEmailAddressOperation({
      store: this.store,
      payload: {
        id: this.id,
        email_address: targetEmailAddress,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async updateAccessViaCollection({
    collectionId,
    roleKind,
  }: {
    collectionId: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new UpdateNoteAclViaCollectionOperation({
      store: this.store,
      payload: {
        id: this.id,
        collection_id: collectionId,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async revokeAccessViaSpaceAccount({
    targetSpaceAccountId,
  }: {
    targetSpaceAccountId: string;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new RevokeNoteAclViaSpaceAccountOperation({
      store: this.store,
      payload: {
        id: this.id,
        space_account_id: targetSpaceAccountId,
      },
    }).execute();
  }

  public async revokeAccessViaEmailAddress({ targetEmailAddress }: { targetEmailAddress: string }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new RevokeNoteAclViaEmailAddressOperation({
      store: this.store,
      payload: {
        id: this.id,
        email_address: targetEmailAddress,
      },
    }).execute();
  }

  public async toggleFavorite() {
    const operation = this.isFavorited
      ? new RemoveNoteFromFavoritesOperation({
          store: this.store,
          payload: { note_id: this.id },
        })
      : new AddNoteToFavoritesOperation({
          store: this.store,
          payload: { note_id: this.id },
        });
    this.memoryQueue.push(operation);
  }

  public addToRecents = async () => {
    this.memoryQueue.push(
      new MarkNoteViewedOperation({
        store: this.store,
        payload: { note_id: this.id },
      })
    );
  };

  public uploadImageAssociatedWithNote({ info }: { info: MemCommonEditorImageInfo }) {
    this.memoryQueue.push(
      new UploadImageExternalOperation({
        store: this.store,
        payload: {
          info,
        },
      })
    );
  }

  public uploadFileAssociatedWithNote({ info }: { info: MemCommonEditorFileInfo }) {
    this.memoryQueue.push(
      new UploadFileExternalOperation({
        store: this.store,
        payload: {
          info,
        },
      })
    );
  }

  public async generateCommonEditorContext(): Promise<MemCommonEditorContext> {
    const uploadImageOperations = (this.store.sync.actionQueue.operationsByOperationKind.get(
      ExternalOperationKind.UPLOAD_IMAGE
    ) ?? []) as UploadImageExternalOperation[];

    const imageInfoMapping: MemCommonEditorContext["images"] = uploadImageOperations.reduce(
      (acc, operation) => {
        const info = operation.payload.info;

        return {
          ...acc,
          [info.imageId]: { info },
        };
      },
      {} as MemCommonEditorContext["images"]
    );

    const uploadFileOperations = (this.store.sync.actionQueue.operationsByOperationKind.get(
      ExternalOperationKind.UPLOAD_FILE
    ) ?? []) as UploadFileExternalOperation[];

    const fileInfoMapping: MemCommonEditorContext["files"] = uploadFileOperations.reduce(
      (acc, operation) => {
        const info = operation.payload.info;

        return {
          ...acc,
          [info.fileId]: { info },
        };
      },
      {} as MemCommonEditorContext["files"]
    );

    const includeDisplayState =
      this.store.spaceAccountFeatureFlags.getFlagStatus("content-understanding") ?? false;
    const cuMapping = await this.store.spaceAccountNoteAttachments.getContentUnderstandingForNote(
      this.id,
      { includeDisplayState }
    );

    for (const [fileId, cuInfo] of Object.entries(cuMapping)) {
      if (!fileInfoMapping[fileId]) {
        fileInfoMapping[fileId] = {} as MemCommonEditorContext["files"][string];
      }
      fileInfoMapping[fileId].contentUnderstanding = cuInfo;
    }

    const assetProxy = await this.buildAssetProxyConfigForContext();

    const context: MemCommonEditorContext = {
      images: imageInfoMapping,
      files: fileInfoMapping,
      assetProxy,
    };

    return context;
  }

  private async buildAssetProxyConfigForContext(): Promise<
    MemCommonEditorAssetProxyConfig | undefined
  > {
    const authStore = this.store.publicAppStore.auth;

    if (!authStore.isStandardMode) {
      return undefined;
    }

    const token = await authStore.assetAccessTokenState.ensureToken();
    if (!token) {
      return undefined;
    }

    const expiresAtIso = authStore.assetAccessTokenState.expiresAt
      ? authStore.assetAccessTokenState.expiresAt.toUTC().toISO()
      : null;

    const baseUrl = getBackendAssetProxyUrl();

    return {
      baseUrl,
      auth: {
        assetAccessToken: token,
        expiresAt: expiresAtIso,
      },
    };
  }

  private updateLabels(primaryLabel: string, secondaryLabel: string) {
    this.primaryLabel = primaryLabel;
    this.secondaryLabel = secondaryLabel;
  }

  private notifyHeadsUpOfAutoGenerationReadiness({
    primaryLabel,
    isEmpty,
  }: {
    primaryLabel: string;
    isEmpty?: boolean;
  }) {
    if (!primaryLabel.trim()) return;
    if (isEmpty) return;

    this.store.headsUp.markNoteHasPrimaryLabelForAutoGeneration(this.id);
  }

  // SUGGESTIONS
  get isSuggestionActive() {
    return !!this.suggestionData;
  }

  get suggestionData(): NoteSuggestionData | undefined {
    return getRenderableSuggestionData({
      suggestionData: this.spaceAccountNote?.modelData.suggestion_data,
    });
  }

  get associatedAudioRecording(): AudioRecordingObservable | undefined {
    return this.store.audioRecordings.getNoteRecording({ noteId: this.id })?.recording;
  }

  get isAudioRecordingActive() {
    return this.store.audio.activeRecordingNoteId === this.id;
  }

  get isAwaitingAudioRecordingTransformation() {
    return this.associatedAudioRecording?.isTransforming ?? false;
  }

  setIsProcessingSuggestion(isProcessingSuggestion: boolean) {
    this.isProcessingSuggestion = isProcessingSuggestion;
  }

  async acceptSuggestion() {
    const suggestionData = this.suggestionData;
    if (!suggestionData) return;
    const spaceAccountNote = this.spaceAccountNote;
    if (!spaceAccountNote) return;

    await new AcceptNoteSuggestionOperation({
      store: this.store,
      payload: {
        note_id: this.id,
        encoded_content_diff: suggestionData.suggested_encoded_content_diff,
      },
      optimisticData: {
        note: this.data,
        spaceAccountNote: spaceAccountNote.data,
      },
    }).execute();

    this.notifyHeadsUpOfAutoGenerationReadiness({
      primaryLabel: suggestionData.suggested_primary_label,
      isEmpty: false,
    });

    this.store.headsUp.handleEditorUpdate({
      noteId: this.id,
      primaryLabel: suggestionData.suggested_primary_label,
      isEmpty: false,
      transactionMetadata: { addToHistory: false },
    });

    // make sure the heads up panel is refreshed
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    if (spaceAccountId) {
      const contextId = resolveSmartContextId({
        spaceAccountId,
        activeNoteId: this.id,
        smartViewKind: SmartViewKind.HeadsUp,
      });

      if (contextId) {
        // Try to refresh the smart view if it exists
        // Note: refreshSmartView requires smartViewId, so we hydrate/generate if needed
        void this.store.headsUp
          .hydrateContext({
            activeNoteId: this.id,
            smartViewKind: SmartViewKind.HeadsUp,
          })
          .then(result => {
            if (result?.id) {
              return this.store.headsUp.refreshSmartView({
                smartViewId: result.id,
                contextId,
              });
            }
            return null;
          })
          .catch(() => {
            // Silently fail if refresh fails
          });
      }
    }

    if (
      suggestionData.kind === "VOICE_SESSION_V1" ||
      suggestionData.kind === "VOICE_SESSION_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, transcriptText, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.VoiceSessionSuggestedEditsAccept, {
        session_id: suggestionData.metadata.session_id,
        captured_audio_id: suggestionData.metadata.captured_audio_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        transcript: transcriptText,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_primary_label: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    } else if (
      suggestionData.kind === "NOTE_CLEANUP_V1" ||
      suggestionData.kind === "NOTE_CLEANUP_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.NoteCleanupAccept, {
        session_id: suggestionData.metadata?.session_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    }
  }

  async rejectSuggestion() {
    const suggestionData = this.suggestionData;
    if (!suggestionData) return;
    const spaceAccountNote = this.spaceAccountNote;
    if (!spaceAccountNote) return;

    await new RejectNoteSuggestionOperation({
      store: this.store,
      payload: { note_id: this.id },
      optimisticData: {
        spaceAccountNote: spaceAccountNote.data,
      },
    }).execute();

    if (
      suggestionData.kind === "VOICE_SESSION_V1" ||
      suggestionData.kind === "VOICE_SESSION_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, transcriptText, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.VoiceSessionSuggestedEditsReject, {
        session_id: suggestionData.metadata.session_id,
        captured_audio_id: suggestionData.metadata.captured_audio_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        transcript: transcriptText,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    } else if (
      suggestionData.kind === "NOTE_CLEANUP_V1" ||
      suggestionData.kind === "NOTE_CLEANUP_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.NoteCleanupReject, {
        session_id: suggestionData.metadata?.session_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    }
  }

  async refineNoteSuggestion(userPrompt: string) {
    if (!this.suggestionData) return;

    const noteId = this.id;
    const sessionId = this.suggestionData.metadata.session_id;

    if (
      this.suggestionData.kind === "VOICE_SESSION_V1" ||
      this.suggestionData.kind === "VOICE_SESSION_REFINE_V1"
    ) {
      const capturedAudioId = this.suggestionData.metadata.captured_audio_id;
      const audioRecordingId = this.suggestionData.metadata.audio_recording_id;
      if (!audioRecordingId) {
        throw new VoiceSuggestionMissingAudioRecordingIdError({
          noteId,
          suggestionKind: this.suggestionData.kind,
          capturedAudioId,
        });
      }
      await refineNoteWithVoice({
        noteId,
        userPrompt,
        audioRecordingId,
        capturedAudioId,
        suggestionData: this.suggestionData,
        store: this.store,
      });
    } else if (
      this.suggestionData.kind === "NOTE_CLEANUP_V1" ||
      this.suggestionData.kind === "NOTE_CLEANUP_REFINE_V1"
    ) {
      await refineNote({
        noteId,
        userPrompt,
        sessionId,
        activeSuggestionMarkdownContent: this.suggestionData.suggested_markdown_content,
        store: this.store,
      });
    }
  }

  async cleanup() {
    return await cleanupNote({
      noteId: this.id,
      store: this.store,
    });
  }
}
const METADATA_ID = "METADATA_CHANGED_ID";
